import useUserData, { UserType } from 'hooks/useUserData';
import {
  useRef,
  useState,
  useEffect,
  KeyboardEvent,
  useMemo,
  useCallback,
  ChangeEvent,
} from 'react';
import useBuildWiseGPTHistoryQuery, {
  BuildWiseGPTHistoryItem,
  PromptSource,
} from './useBuildWiseGPTHistoryQuery';
import _, { set } from 'lodash';
import { toast } from 'react-toastify';
import {
  addChatToSessionStorage,
  attachFiles,
  catchErrorMessage,
  deleteChatFromSessionStorage,
  generateContentForAWSBedrockChat,
  generateContentForGCPChat,
  getChatFromSessionStorage,
} from 'utility/helpers';
import moment from 'moment';
import UserAPI from 'api/UserAPI';
import { updateUserDetailsCache } from '@buildwise/libs/people/queries/useUserProfileData';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BUILDWISE_GPT_CHAT, BUILDWISE_GPT_TEXT, LOGIN } from 'constants/routes';
import { EventStreamContentType, fetchEventSource } from '@microsoft/fetch-event-source';
import {
  API_CLIENT_ID,
  GEN_AI_SERVICE_ENDPOINTS,
  GEN_AI_SERVICE_URL,
  JAVA_SERVICE_ENDPOINTS,
  PYTHON_SERVICE_ENDPOINTS,
  PYTHON_SERVICE_URL,
} from '@buildwise/libs/services';
import useAuthStore from 'store/useAuthStore';
import { shallow } from 'zustand/shallow';
import { BedrockFormData } from '../BuildWiseGPTForm';
import { AttachmentType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { queryClient } from 'config/queryClient';
import {
  AWS_BEDROCK_CHAT_ATTACHMENT_TYPES,
  DEFAULT_GPT_CHAT_SERVICE_PROVIDER,
  DOCUMENT_TYPES,
  GCP_VERTEX_AI_ATTACHMENT_TYPES,
  IMAGE_TYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
} from 'components/utils/constants';

export type PromptHistoryItem = {
  date: string;
  data: BuildWiseGPTHistoryItem[];
};

export const filterHistoryByDate = (
  historyData: BuildWiseGPTHistoryItem[],
): PromptHistoryItem[] => {
  const chatByDate: Record<string, BuildWiseGPTHistoryItem[]> = {};

  historyData
    .filter((item) => !item.promptSource || item.promptSource === PromptSource.BuildWiseGPT)
    .forEach((item) => {
      const dateWithoutTime = moment(item.createdAt).format('YYYY-MM-DD');
      chatByDate[dateWithoutTime] = chatByDate[dateWithoutTime]
        ? [...chatByDate[dateWithoutTime], item]
        : [item];
    });

  const allDates = Object.keys(chatByDate);

  return _.orderBy(
    allDates.map((item) => ({
      date: item,
      data: _.orderBy(chatByDate[item], 'createdAt'),
    })),
    'date',
  );
};

class RetriableError extends Error {}
class FatalError extends Error {}

function useBuildWiseGPT({ type }: { type: string }) {
  const { user, companyId } = useUserData();
  const userAuthDetails = useAuthStore((state) => state, shallow);
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  const { buildwiseGPTHistory, updatePromptHistoryCache } = useBuildWiseGPTHistoryQuery();
  const [value, setValue] = useState(state?.prompt || '');
  const [collpasedDates, setCollapsedDates] = useState<string[]>([]);
  const [openFullViewModal, setFullViewModal] = useState(!!state?.prompt || false);
  const [attachments, setAttachments] = useState<AttachmentType[]>([]);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [searchParams] = useSearchParams();
  const [serviceProvider, setServiceProvider] = useState<string | null>(null);
  const [acceptedFiles, setAcceptedFiles] = useState<string[]>([]);

  const sessionKeyName = useMemo(() => {
    return BUILDWISE_GPT_CHAT.includes(pathname) ? 'bedrockChatForm' : 'bedrockTextForm';
  }, [pathname]);

  const [history, setHistory] = useState<BuildWiseGPTHistoryItem[] | []>(() => {
    if (type === 'text') {
      return [
        {
          _id: _.uniqueId(),
          prompt: decodeURIComponent(searchParams.get('q') || ''),
          createdAt: new Date().toISOString(),
          createdBy: user?.username || '',
          genAIResponse: '',
          referenceFiles: [],
          isStreaming: false,
        },
      ];
    }
    return [];
  });

  const memoizedHistory = useMemo(() => {
    if (type === 'text') {
      return buildwiseGPTHistory;
    }
    return history;
  }, [type, buildwiseGPTHistory, history]);

  const historyData = useMemo(() => filterHistoryByDate(memoizedHistory), [memoizedHistory]);

  useEffect(() => {
    setAcceptedFiles(
      serviceProvider === 'aws'
        ? [
            ...AWS_BEDROCK_CHAT_ATTACHMENT_TYPES.DOCUMENT_TYPES,
            ...AWS_BEDROCK_CHAT_ATTACHMENT_TYPES.IMAGE_TYPES,
            ...AWS_BEDROCK_CHAT_ATTACHMENT_TYPES.VIDEO_TYPES,
          ]
        : serviceProvider === 'gcp'
          ? GCP_VERTEX_AI_ATTACHMENT_TYPES
          : [...IMAGE_TYPES, ...DOCUMENT_TYPES],
    );
  }, [serviceProvider]);

  useEffect(() => {
    if (type === 'chat') {
      setHistory(
        _.orderBy(getChatFromSessionStorage({ companyId, key: 'bedrockChat' }), 'createdAt'),
      );
    }
  }, [type]);
  useEffect(() => {
    if (containerRef.current && (history.length > 1 || buildwiseGPTHistory.length > 1)) {
      containerRef.current.scrollIntoView();
    }
  }, [containerRef, history.length, buildwiseGPTHistory.length]);

  const postPrompt = async (value: string, isRegenerating?: boolean) => {
    if (value.trim()) {
      const { llm_model_name, token_size }: BedrockFormData = {
        ...JSON.parse(String(sessionStorage.getItem(sessionKeyName))),
      };
      const serviceProvider: string = llm_model_name?.provider || DEFAULT_GPT_CHAT_SERVICE_PROVIDER;

      if (type === 'chat') {
        setValue('');
        setAttachments([]);
        const _history = [
          ...history,
          {
            _id: _.uniqueId(),
            createdAt: new Date().toISOString(),
            createdBy: user?.username || '',
            genAIResponse: '',
            prompt: value.trim(),
            referenceFiles: [],
          },
        ];

        const content =
          serviceProvider === 'gcp'
            ? await generateContentForGCPChat(companyId, value, 'bedrockChat', attachments)
            : await generateContentForAWSBedrockChat(companyId, value, 'bedrockChat', attachments);
        const payload = {
          ...content,
          companyId,
          user_query: value.trim(),
          ...(llm_model_name && { model: llm_model_name.value }),
          ...(token_size && { max_tokens: token_size }),
          temparature: 0.7,
        };
        setHistory(_history);
        setLoading(true);
        try {
          await fetchEventSource(
            `${PYTHON_SERVICE_URL}${PYTHON_SERVICE_ENDPOINTS.CHAT_WITH_BEDROCK_WITH_ATTACHMENTS}`,
            {
              openWhenHidden: true,
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Client-id': API_CLIENT_ID,
                Authorization: `Bearer ${userAuthDetails?.accessToken || ''}`,
                'Company-id': companyId,
              },
              body: JSON.stringify(payload),
              async onopen(response) {
                if (
                  response.ok &&
                  response.headers.get('content-type')?.includes(EventStreamContentType)
                ) {
                  return; // everything's good
                } else if (
                  response.status >= 400 &&
                  response.status < 500 &&
                  response.status !== 429
                ) {
                  // client-side errors are usually non-retriable:
                  throw new FatalError();
                } else {
                  throw new RetriableError();
                }
              },
              onmessage(msg) {
                // if the server emits an error message, throw an exception
                // so it gets handled by the onerror callback below.
                if (msg.event === 'FatalError') {
                  throw new FatalError(msg.data);
                }

                if (msg.data) {
                  setLoading(false);
                  const parsedData = JSON.parse(msg.data);
                  setHistory((prevState) => {
                    const newHistory = [...prevState];
                    const lastItem = newHistory[newHistory.length - 1];
                    newHistory[newHistory.length - 1] = {
                      ...lastItem,
                      isStreaming: true,
                      genAIResponse: `${lastItem.genAIResponse}${parsedData?.message || ''}`,
                      _id: parsedData?.chatId || lastItem._id,
                      ...content,
                    };
                    addChatToSessionStorage({
                      chatItem: newHistory[newHistory.length - 1],
                      companyId,
                      key: 'bedrockChat',
                    });
                    return newHistory;
                  });
                }
              },
              onclose() {
                setHistory((prevState) => {
                  const newHistory = [...prevState];
                  const lastItem = newHistory[newHistory.length - 1];
                  newHistory[newHistory.length - 1] = {
                    ...lastItem,
                    messages: [
                      ...(lastItem?.messages || []),
                      {
                        [serviceProvider === 'gcp' ? 'parts' : 'content']: [
                          {
                            text: lastItem.genAIResponse,
                          },
                        ],
                        role: 'assistant',
                      },
                    ],
                    isStreaming: false,
                  };
                  addChatToSessionStorage({
                    chatItem: newHistory[newHistory.length - 1],
                    companyId,
                    key: 'bedrockChat',
                  });
                  return newHistory;
                });
              },
              onerror(error) {
                setLoading(false);
                if (error?.status === 423) {
                  const errorMsg = error?.response?.data?.errorPresent
                    ? catchErrorMessage(error)
                    : error?.message || 'User is deactivated. Please contact support team.';
                  toast.error(errorMsg);
                  userAuthDetails.resetState();
                  navigate(LOGIN);
                } else {
                  toast.error(error?.message || 'Something went wrong. Please try again.');
                  throw error;
                }
              },
            },
          );
        } catch (error) {
          console.warn('CHAT_WITH_BEDROCK_ERROR', error);
        } finally {
          setLoading(false);
        }
      } else {
        setValue('');
        setAttachments([]);
        setCollapsedDates((prevState) => {
          const updatedState = [...prevState];
          const dateWithoutTime = moment().format('YYYY-MM-DD');
          const index = updatedState.indexOf(dateWithoutTime);
          if (index > -1) {
            updatedState.splice(index, 1);
          }

          return updatedState;
        });
        const lastPrompt = _.orderBy(buildwiseGPTHistory, 'createdAt', 'desc')[0];
        const chat = {
          _id: _.uniqueId(),
          createdAt: new Date().toISOString(),
          createdBy: user?.username || '',
          genAIResponse: '',
          prompt: value.trim(),
          promptSource: PromptSource.BuildWiseGPT,
        };

        if (isRegenerating) {
          updatePromptHistoryCache({
            action: 'update',
            chat: {
              ...lastPrompt,
              genAIResponse: '',
            },
          });
        } else {
          updatePromptHistoryCache({
            action: 'add',
            chat,
          });
        }
        setLoading(true);
        try {
          const searchParams = new URLSearchParams({
            company_id: companyId,
            ...(llm_model_name && { model_name: llm_model_name.value }),
            ...(token_size && { max_tokens: token_size }),
          });

          const formData = new FormData();
          formData.append('user_prompt', value.trim());
          if (attachments?.length) {
            attachments.forEach((file) => {
              formData.append('files', file.file);
            });
          }
          await fetchEventSource(
            `${GEN_AI_SERVICE_URL}${GEN_AI_SERVICE_ENDPOINTS.POST_PROMPT}?${searchParams.toString()}`,
            {
              openWhenHidden: true,
              method: 'POST',
              headers: {
                'Client-id': API_CLIENT_ID,
                Authorization: `Bearer ${userAuthDetails?.accessToken || ''}`,
                'Company-id': companyId,
              },
              body: formData,
              async onopen(response) {
                if (
                  response.ok &&
                  response.headers.get('content-type')?.includes(EventStreamContentType)
                ) {
                  return; // everything's good
                } else if (
                  response.status >= 400 &&
                  response.status < 500 &&
                  response.status !== 429
                ) {
                  // client-side errors are usually non-retriable:
                  throw new FatalError();
                } else {
                  throw new RetriableError();
                }
              },
              onmessage(msg) {
                // if the server emits an error message, throw an exception
                // so it gets handled by the onerror callback below.
                if (msg.event === 'FatalError') {
                  throw new FatalError(msg.data);
                }

                if (msg.data) {
                  setLoading(false);
                  const parsedData = JSON.parse(msg.data);
                  const queryStatus = queryClient.getQueryState<any>([
                    JAVA_SERVICE_ENDPOINTS.GET_BUILDWISE_GPT_HISTORY,
                    user?.username,
                  ]);
                  const prevGenAIResponse =
                    queryStatus?.data?.data?.promptHistoryList?.[0]?._id === chat._id
                      ? queryStatus?.data?.data?.promptHistoryList?.[0]?.genAIResponse
                      : '';
                  updatePromptHistoryCache({
                    action: 'update',
                    chat: {
                      ...(isRegenerating ? lastPrompt : { ...chat }),
                      genAIResponse:
                        parsedData?.metadata?.genAIResponse ||
                        `${prevGenAIResponse}${parsedData?.message || ''}`,
                      isStreaming: !parsedData?.metadata,
                    },
                  });
                }
              },
              onclose() {
                if (state?.prompt) {
                  navigate(BUILDWISE_GPT_TEXT);
                }
              },
              onerror(error) {
                if (error?.status === 423) {
                  const errorMsg = error?.response?.data?.errorPresent
                    ? catchErrorMessage(error)
                    : error?.message || 'User is deactivated. Please contact support team.';
                  toast.error(errorMsg);
                  userAuthDetails.resetState();
                  navigate(LOGIN);
                } else {
                  throw error; // rethrow to stop the operation
                }
              },
            },
          );

          // const { data } = await BuildWiseGPTAPI.postPrompt({
          //   prompt: value.trim(),
          //   companyId,
          //   files: attachments,
          //   // ...(isRegenerating && { regenerateFlag: true }),
          // });
        } catch (error) {
          console.warn(error);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const onSend = (_value: string) => {
    postPrompt(_value);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(-1, -1);
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!event.shiftKey && event.key === 'Enter' && value) {
      event.preventDefault();
      postPrompt(value);
    }
  };

  const onFullModalMinimized = (_value: string) => {
    setValue(_value);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(-1, -1);
        inputRef.current.focus();
      }
    }, 0);
  };

  const bookmarkPrompt = async (prompt: BuildWiseGPTHistoryItem) => {
    const oldUser = { ...user };
    try {
      const updatedPromptIds = [...(user?.gptPromptIds || [])];
      if (prompt.isBookMarked) {
        updatedPromptIds.splice(updatedPromptIds.indexOf(prompt._id), 1);
      } else {
        updatedPromptIds.push(prompt._id);
      }
      updateUserDetailsCache({ ...user, gptPromptIds: updatedPromptIds } as UserType);

      await UserAPI.editUser({
        ...user,
        gptPromptHistoryIds: updatedPromptIds,
      });
    } catch (error) {
      updateUserDetailsCache(oldUser as UserType);
      toast.error(catchErrorMessage(error));
    }
  };

  const regeneratePrompt = (prompt?: string) => {
    if (prompt) {
      setValue(prompt);
    } else {
      let lastPrompt;
      if (type === 'chat') {
        lastPrompt = _.orderBy(history, 'createdAt', 'desc')[0];
      } else {
        lastPrompt = _.orderBy(
          buildwiseGPTHistory.filter(
            (item) => !item.promptSource || item.promptSource === PromptSource.BuildWiseGPT,
          ),
          'createdAt',
          'desc',
        )[0];
      }
      setValue(lastPrompt.prompt);
    }
    setFullViewModal(true);
  };

  const onClose = () => {
    setValue('');
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(-1, -1);
        inputRef.current.focus();
      }
    }, 0);
  };

  const onNewSession = () => {
    deleteChatFromSessionStorage({ companyId, key: 'bedrockChat' });
    setHistory([]);
    setValue('');
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(-1, -1);
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleFileUploads = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = [...((event?.target?.files || []) as File[])];

      // Filter files that are ≤ 5MB
      const validFiles = files.filter((file) => file.size <= MAX_FILE_SIZE_BYTES);

      // Alert or notify for invalid files
      const invalidFiles = files.filter((file) => file.size > MAX_FILE_SIZE_BYTES);
      if (invalidFiles.length > 0) {
        toast.error(
          `The following files exceed ${MAX_FILE_SIZE_MB}MB:\n\n` +
            invalidFiles.map((f) => `${f.name}`).join('\n\n'),
        );
      }

      if (validFiles.length === 0) {
        return;
      }

      // Proceed with valid files
      attachFiles({
        newAttachments: validFiles,
        attachments,
        setAttachments: (_attachments: AttachmentType[]) => {
          setAttachments(_attachments);
        },
      });
    },
    [attachments],
  );

  const handleRemoveAttachment = useCallback((attachment: AttachmentType) => {
    setAttachments?.((prevState) => {
      const updatedState = prevState.filter(
        (_attachment) => _attachment.file.name !== attachment.file.name,
      );
      setAttachments(updatedState);

      return updatedState;
    });
  }, []);

  return {
    attachments,
    collpasedDates,
    containerRef,
    history,
    historyData,
    inputRef,
    loading,
    openFullViewModal,
    value,
    serviceProvider,
    acceptedFiles,
    setServiceProvider,
    bookmarkPrompt,
    handleFileUploads,
    handleKeyDown,
    handleRemoveAttachment,
    onClose,
    onFullModalMinimized,
    onNewSession,
    onSend,
    regeneratePrompt,
    setAttachments,
    setCollapsedDates,
    setFullViewModal,
    setValue,
  };
}

export default useBuildWiseGPT;
